import { ClipboardCopyButton } from '@neo/blocks/clipboard-copy-button'
import { Button, ButtonWithLoader } from '@neo/index'
import { Popover, PopoverTrigger } from '@neo/primitives/popover'
import { Tooltip } from '@neo/primitives/tooltip'
import type { AxiosError } from 'axios'
import { Sparkles, SquareAsterisk } from 'lucide-react'
import { useState } from 'react'
import { CodeEditor } from 'v2/shared/components/code-editor'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { errorToast } from 'v2/utils/toasts'
import { useGenerateFunctionSchemaMutation } from '../../queries'
import { CodeGenerationModal } from '../ai-agents/code-generation-modal-v2'
import { SecretVariablesContent } from '../ai-agents/secret-variables-popover-v2'

interface McpCodeEditorProps {
  code: string
  name: string
  description: string
  onChange: (code: string) => void
  onSchema: (schema: Record<string, unknown>) => void
  onApply: (
    code: string,
    schema: Record<string, unknown>,
    name: string,
    description: string,
  ) => void
}

export const McpCodeEditor = ({
  code,
  name,
  description,
  onChange,
  onSchema,
  onApply,
}: McpCodeEditorProps) => {
  const [secretsOpen, setSecretsOpen] = useState(false)
  const [generateOpen, setGenerateOpen] = useState(false)
  const { mutate, isPending } = useGenerateFunctionSchemaMutation()

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-s-600 text-secondary">
          Code <span className="text-s-400 italic text-tertiary">(Python3)</span>
        </span>
        <div className="flex items-center gap-1">
          <ClipboardCopyButton
            textToCopy={code}
            tooltipText="Copy code"
            className="size-4 text-secondary"
          />
          <Popover open={secretsOpen} onOpenChange={setSecretsOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="text-s-600 text-secondary p-1 hover:bg-accent rounded-sm data-[state=open]:bg-primary data-[state=open]:text-primary-foreground"
                aria-label="View secret variables"
                data-testid="button-mcp-secret-variables"
              >
                <Tooltip content="Add secret variables">
                  <SquareAsterisk className="size-4" />
                </Tooltip>
              </button>
            </PopoverTrigger>
            {secretsOpen && (
              <SecretVariablesContent
                code={code}
                onInsert={onChange}
                onClose={() => setSecretsOpen(false)}
              />
            )}
          </Popover>
          <Button
            type="button"
            variant="outline"
            size="xs"
            onClick={() => setGenerateOpen(true)}
            data-testid="button-mcp-generate-code"
          >
            <Sparkles className="size-4 mr-2" />
            Generate code
          </Button>
        </div>
      </div>
      <CodeEditor
        language="python"
        value={code}
        onChange={onChange}
        className="border border-st2 rounded-md overflow-auto"
      />
      <ButtonWithLoader
        loading={isPending}
        type="button"
        variant="outline"
        size="sm"
        className="mt-2"
        data-testid="button-mcp-generate-schema"
        onClick={() =>
          mutate(
            { code, function_name: name, function_description: description },
            // TanStack drops these once the editor unmounts, so a response for a source
            // that was switched away from never refills its cleared fields.
            {
              onSuccess: (data) => onSchema(data.function_schema),
              onError: (error) => {
                errorToast({
                  title: 'Failed to generate function schema',
                  description: getErrorText(error as AxiosError),
                })
              },
            },
          )
        }
      >
        Generate Function Schema
      </ButtonWithLoader>
      <CodeGenerationModal
        isOpen={generateOpen}
        onClose={() => setGenerateOpen(false)}
        name={name}
        description={description}
        onApply={onApply}
      />
    </div>
  )
}

export const McpFunctionSchemaEditor = ({
  value,
  onChange,
}: {
  value: unknown
  onChange: (value: unknown) => void
}) => {
  if (!value) return null
  const json = typeof value === 'string' ? value : JSON.stringify(value, null, 2)
  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-s-600 text-secondary">
          Function Schema <span className="text-s-400 italic text-tertiary">(JSON)</span>
        </span>
        <ClipboardCopyButton
          textToCopy={json}
          tooltipText="Copy function schema"
          className="mr-1 size-4 text-secondary"
        />
      </div>
      <CodeEditor
        language="json"
        value={json}
        className="border border-st2 rounded-md overflow-auto bg-background"
        onChange={(code) => {
          try {
            onChange(JSON.parse(code))
          } catch {
            onChange(code)
          }
        }}
      />
    </div>
  )
}
